import { PERMISSION_KEYS, type PermissionKey } from "./permission-keys.js";

export * from "./permission-keys.js";

const KEY_SET: ReadonlySet<string> = new Set(PERMISSION_KEYS);
export const isPermissionKey = (k: unknown): k is PermissionKey => typeof k === "string" && KEY_SET.has(k);

// One feature row of the permission editor: a view key, optional add/update/delete keys and extra keys.
// The seven tabs and their rows follow the audited legacy permission screen (report img/en_permissions.jpg).
export interface PermissionRow {
  view: PermissionKey;
  ops: Partial<Record<"add" | "update" | "delete", PermissionKey>>;
  extras: PermissionKey[];
}
export interface PermissionTab {
  key: string;
  rows?: PermissionRow[];
  // Report tabs list keys in groups by prefix (r1 … r9) instead of rows.
  groups?: { prefix: string; label: string }[];
}

const row = (view: PermissionKey, one: string | null, extras: PermissionKey[] = [], ops: ("Add" | "Update" | "Delete")[] = ["Add", "Update", "Delete"]): PermissionRow => {
  const o: PermissionRow["ops"] = {};
  if (one) for (const op of ops) o[op.toLowerCase() as "add" | "update" | "delete"] = (one + op) as PermissionKey;
  return { view, ops: o, extras };
};

export const PERMISSION_TABS: PermissionTab[] = [
  { key: "invoices", rows: [row("invoiceSales", "invoiceSale"), row("invoiceSaleRefunds", "invoiceSaleRefund"), row("invoicePurchases", "invoicePurchase"), row("invoicePurchaseRefunds", "invoicePurchaseRefund"), row("accounts", "account")] },
  { key: "warehouses", rows: [row("invoiceBeginningInventories", "invoiceBeginningInventory"), row("invoiceTransfers", "invoiceTransfer"), row("invoiceInventories", "invoiceInventory"), row("invoiceCompromises", "invoiceCompromise"),
    row("invoiceConsumptions", "invoiceConsumption"), row("invoiceCorrupteds", "invoiceCorrupted"), row("items", "item"), row("itemCategories", "itemCategory"), row("itemKinds", "itemKind"), row("itemUnits", "itemUnit", ["itemUnitBarcodePrint"]), row("units", "unit"), row("warehouses", "warehouse")] },
  { key: "accounts", rows: [row("journals", "journal"), row("journalEntries", "journalEntry"), row("monies", "money", ["moneyVerify"]), row("partners", "partner"), row("transfers", "transfer"), row("costCenters", "costCenter"), row("chartOfAccountLevel2s", "chartOfAccountLevel2"), row("chartOfAccountLevel3s", "chartOfAccountLevel3")] },
  { key: "assets", rows: [row("assets", "asset"), row("assetKinds", "assetKind"), row("assetInvoices", "assetInvoice"), row("assetDepreciations", "assetDepreciation", ["assetDepreciationDeport", "assetDepreciationCancelDeport"], ["Delete"])] },
  { key: "hr", rows: [row("staffers", "staffer"), row("allowances", "allowance")] },
  { key: "settings", rows: [row("stores", "store", ["storeBarcode"]), row("printers", "printer"), row("taxTypes", "taxType"), row("users", "user", ["userPrivileges", "userHistory"]), row("alerts", "alert"), row("home", null), row("setting", null), row("clientArea", null)] },
  { key: "reports", groups: [{ prefix: "r1", label: "repAccounting" }, { prefix: "r2", label: "repFinancial" }, { prefix: "r3", label: "repInvoices" }, { prefix: "r4", label: "repEmployees" }, { prefix: "r5", label: "repParties" }, { prefix: "r6", label: "repTreasury" }, { prefix: "r7", label: "repTaxes" }, { prefix: "r8", label: "repWarehouses" }, { prefix: "r9", label: "repAssets" }] }
];

export const rowKeys = (r: PermissionRow): PermissionKey[] => [r.view, ...Object.values(r.ops), ...r.extras] as PermissionKey[];
export const tabKeys = (tb: PermissionTab): PermissionKey[] =>
  tb.groups ? PERMISSION_KEYS.filter((k) => /^r\d/.test(k)) : (tb.rows ?? []).flatMap(rowKeys);

// Delta rule (to confirm with the business, remaining-issues.md item 8): any add / update / delete / extra key
// needs the feature's view key, so a set is normalised by turning view on for every dependent key that is on.
// Applied on the server whenever permissions are saved; the editor applies the same rule while switching.
export function normalizePermissions(keys: Iterable<string>): PermissionKey[] {
  const on = new Set<PermissionKey>();
  for (const k of keys) if (isPermissionKey(k)) on.add(k);
  for (const tb of PERMISSION_TABS) for (const r of tb.rows ?? []) {
    if (rowKeys(r).some((k) => k !== r.view && on.has(k))) on.add(r.view);
  }
  return PERMISSION_KEYS.filter((k) => on.has(k));
}
